import axios from 'axios'
import { ClientRateconRevision } from '@numeo/types-main-web-app'
import { Badge, Input } from '@/components/ds2'

/** The revision's presentational pieces, shared by the board's review modal and the general-agent chat's revision card,
 *  so a revision reads the same wherever it is reviewed. */

/** Someone else resolved this exact revision first — a second tab, a stale modal left open after
 *  the banner's list refreshed, or (rarely) a genuine double-click race. The backend's PENDING
 *  filter already prevented the double-apply; the only thing left to do here is tell the user why
 *  it failed (not "try again" — retrying fails identically) and let them close back out to a
 *  fresh, accurate list instead of staring at a dead modal. */
export const isAlreadyResolvedError = (err: unknown): boolean => axios.isAxiosError(err) && err.response?.status === 404

export interface DiffRow {
    /** The raw `changedFields` entry (e.g. `'rate'`, `'route[1].date'`) — unique per row, unlike `label`
     *  which collapses to the same text for same-field changes across multiple stops (e.g. two delivery
     *  stops both changing `date` would both render as "Delivery date"). Used as the React list key. */
    key: string
    label: string
    original: string
    revised: string
    delta: string
    deltaTone: 'positive' | 'negative' | 'neutral'
    /** Whether edit-merge may offer an input for this row. An address is not free text the backend
     *  can take verbatim — it has to geocode — so a moved stop is accepted as the con states it or
     *  not at all. */
    editable: boolean
}

const deltaToneClass: Record<DiffRow['deltaTone'], string> = {
    positive: 'text-success font-semibold',
    negative: 'text-danger font-semibold',
    neutral: 'text-foreground-muted',
}

/** FINANCIAL or SCHEDULE, and the rate's direction when it moved. */
export const RateconRevisionKindBadges = ({ revision }: { revision: Pick<ClientRateconRevision, 'v1' | 'v2' | 'changedFields'> }) => {
    const isFinancial = revision.changedFields.includes('rate')
    const rateDelta = revision.v2.rate - revision.v1.rate
    const direction = isFinancial && rateDelta !== 0 ? (rateDelta > 0 ? 'increase' : 'decrease') : null
    return (
        <>
            <Badge variant="soft" type={isFinancial ? 'accent' : 'default'} size="md" label={isFinancial ? 'FINANCIAL' : 'SCHEDULE'} />
            {direction && (
                <Badge
                    variant="soft"
                    type={direction === 'increase' ? 'success' : 'danger'}
                    size="md"
                    label={direction === 'increase' ? '▲ INCREASE' : '▼ DECREASE'}
                />
            )}
        </>
    )
}

interface RateconRevisionDiffTableProps {
    revision: Pick<ClientRateconRevision, 'routeShapeChanged'>
    rows: DiffRow[]
    /** Edit-merge: the editable rows take an input holding `editValues[row.key]`. */
    isEditing?: boolean
    editValues?: Record<string, string>
    onEditValue?: (key: string, value: string) => void
}

/** The field-level diff: each changed field's original, revised value and delta. */
export const RateconRevisionDiffTable = ({ revision, rows, isEditing = false, editValues = {}, onEditValue }: RateconRevisionDiffTableProps) => (
    /* Bounded so stepping between a 2-field and a 9-field revision doesn't resize the
       dialog (it is centre-anchored, so a height change visibly shifts it). Long diffs
       scroll inside this box instead of growing it. */
    <div className="max-h-[320px] min-h-[132px] overflow-auto rounded-xl">
        <table className="w-full min-w-[720px] table-fixed text-sm">
            <colgroup>
                <col className="w-[20%]" />
                <col className="w-[29%]" />
                <col className="w-[6%]" />
                <col className="w-[32%]" />
                <col className="w-[13%]" />
            </colgroup>
            <thead className="sticky top-0 z-[1] bg-surface">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                    <th className="px-3 py-2.5">Field</th>
                    <th className="px-3 py-2.5">Original v1</th>
                    <th className="px-2 py-2.5" />
                    <th className="px-3 py-2.5">{isEditing ? 'Merged value' : 'Revised v2'}</th>
                    <th className="px-3 py-2.5">Δ</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-border">
                {rows.length === 0 ? (
                    <tr>
                        <td className="px-3 py-3 text-foreground-muted" colSpan={5}>
                            {revision.routeShapeChanged
                                ? 'No field can be applied automatically — see the route below.'
                                : 'No tracked field changed. Accepting makes this rate confirmation the load’s document.'}
                        </td>
                    </tr>
                ) : (
                    rows.map((row) => (
                        <tr key={row.key} className="bg-accent-soft/70">
                            <td className="px-3 py-3 align-top font-medium leading-5 text-foreground">{row.label}</td>
                            <td className="px-3 py-3 align-top leading-5 text-foreground-muted">
                                <span className="whitespace-normal break-words line-through decoration-foreground-muted">{row.original}</span>
                            </td>
                            <td className="px-2 py-3 text-center align-top text-foreground-muted">→</td>
                            <td className="px-3 py-3 align-top font-semibold leading-5 text-foreground">
                                {isEditing && row.editable ? (
                                    <Input
                                        inputType={row.key === 'rate' ? 'number' : 'text'}
                                        value={editValues[row.key] ?? ''}
                                        onChange={(e) => onEditValue?.(row.key, e.target.value)}
                                        aria-label={`Merged ${row.label}`}
                                        style={{ width: row.key === 'rate' ? 140 : 260 }}
                                    />
                                ) : (
                                    <span className="whitespace-normal break-words">{row.revised}</span>
                                )}
                            </td>
                            <td className="px-3 py-3 align-top">
                                <span
                                    className={`inline-flex rounded-full bg-surface px-2.5 py-1 text-sm font-medium leading-5 ${deltaToneClass[row.deltaTone]}`}
                                >
                                    {isEditing ? '—' : row.delta}
                                </span>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
)

/** Whose paperwork this actually is. Only ever set on a hand-filed document, and only when it names a
 *  different load: reviewing it then means deciding whether to move THIS load's rate and stops onto
 *  what ANOTHER load's confirmation says. Renders nothing otherwise. */
export const RateconRevisionDocumentNotice = ({ revision }: { revision: Pick<ClientRateconRevision, 'extractedDocumentId' | 'rateConfDocumentId'> }) =>
    revision.extractedDocumentId ? (
        <div className="mt-4 flex items-start gap-3 rounded-xl bg-danger-soft px-3 py-2.5 text-sm">
            <Badge variant="soft" type="danger" size="md" label="DOCUMENT" style={{ flexShrink: 0 }} />
            <span className="text-danger-soft-foreground">
                This rate confirmation names load <strong>{revision.extractedDocumentId}</strong>, not load {revision.rateConfDocumentId}. It was filed here by
                hand — check it belongs on this load before accepting, because accepting applies its rate and stops to this one.
            </span>
        </div>
    ) : null
